// React
import * as React from 'react';

// Redux
import { connect } from 'react-redux';

// Router
import { Router as Router, Redirect, Route } from 'react-router-dom';
import createBrowserHistory from 'history/createBrowserHistory';

// MainStore
import { MainStore } from './stores/MainStore';

// MuiTheme
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';

// Components
import Fade from '@material-ui/core/Fade';
import Icon from '@material-ui/core/Icon';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Navigation from './components/Navigation';
import AppBarTop from './components/AppBarTop';

// Pickers
import MuiPickersUtilsProvider from 'material-ui-pickers/utils/MuiPickersUtilsProvider';
import DateFnsUtils from 'material-ui-pickers/utils/date-fns-utils';
import frCH from 'date-fns/esm/locale/fr-CH';

// Views
import Login from './views/Login';
import Home from './views/Home';
import Gallery from './views/Gallery';
import GalleryProfile from './views/GalleryProfile';
import History from './views/History';
import HistoryProfile from './views/HistoryProfile';
import Comite from './views/Comite';
import ComiteProfile from './views/ComiteProfile';
import Texts from './views/Texts';
import TextsProfile from './views/TextsProfile';
import Notification from './components/Notification';
import Activity from './views/Activity';

// Import React FilePond
import { registerPlugin } from "react-filepond";

// Import FilePond styles
import "filepond/dist/filepond.min.css";

import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImageCrop from 'filepond-plugin-image-crop';
import FilePondPluginImageResize from 'filepond-plugin-image-resize';
import FilePondPluginImageTransform from 'filepond-plugin-image-transform';

// Register the plugin
registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImageCrop, FilePondPluginImageResize, FilePondPluginImageTransform);

// Functions
import { getURLByEnv } from './framework/utils/URL';
import { NetworkManager } from './managers/NetworkManager';
import ActivityProfile from './views/ActivityProfile';
import Inside from './views/Inside';
import InsideProfile from './views/InsideProfile';
import Bookings from './views/Bookings';
import Camp from './views/Camp';
import CampProfile from './views/CampProfile';
import CampEdition from './views/CampEdition';
import CampEditionProfile from './views/CampEditionProfile';
import Retrospective from './views/Retrospective';
import News from './views/News';
import NewsProfile from './views/NewsProfile';
import BookingsProfile from './views/BookingsProfile';

interface State {
  open: boolean;
}

interface Props {
  navigation: any;
  notification: any;
  overlay: any;
  user: any;
  history: any;
}

const theme = createMuiTheme({
  palette: {
    primary: {
      contrastText: '#fff',
      dark: '#0D47A1',
      light: '#90CAF9',
      main: '#1976D2'
    },
    secondary: {
      contrastText: '#000',
      dark: '#ba000d',
      light: '#ff7961',
      main: '#f44336'
    }
  }
});

const history = createBrowserHistory();

const PrivateRoute = ({ component: Component, isConnected, ...rest }: any) => (
  <Route {...rest} render={(props) => (
    isConnected
      ? <Component {...props} />
      : <Redirect to={`${getURLByEnv()}/Connexion`} />
  )} />
);

/**
 * @description React App
 * @author Lucas Fridez <lucas@fridez.dev>
 * @export
 * @class App
 * @extends {React.Component}
 */
export default connect((state) => state)(class App extends React.Component<Props, State> {

  /**
   * Creates an instance of App.
   * @author Lucas Fridez <lucas@fridez.dev>
   * @param {*} props
   * @memberof App
   */
  constructor(props: any) {
    super(props);
  }

  componentWillMount() {
    if (NetworkManager.getUserCredentials() !== undefined) {
      MainStore.loginUser(NetworkManager.getUserCredentials());
    }
  }

  /**
   * @description Render the WebApp in the Browser
   * @author Lucas Fridez <lucas@fridez.dev>
   * @returns
   * @memberof App
   */
  public render() {
    return (
      <Router history={history}>
        <MuiThemeProvider theme={theme}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frCH}>
            <div className="App">
              <Navigation />
              <div className={this.props.navigation.navigation ? 'container-reduced' : 'container'}>
                <AppBarTop />
                {this.props.navigation.backShown && <span className="back-btn mouse-hover"
                  onClick={() => {
                    MainStore.goBack(history.location.pathname);
                    history.goBack();
                  }}>
                  <Icon>keyboard_backspace</Icon>
                  <Typography variant="subheading">Retour</Typography>
                </span>}
                <div role="main">
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/`} component={Home} />
                  <Route exact={true} path={`${getURLByEnv()}/Connexion`} component={Login} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Galeries`} component={Gallery} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Galeries/Créer`} component={GalleryProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Galeries/Modifier/:id`} component={GalleryProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Camps`} component={Camp} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Camps/Créer`} component={CampProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Camps/Modifier/:id`} component={CampProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/EditionDeCamps`} component={CampEdition} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/EditionDeCamps/Créer`} component={CampEditionProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/EditionDeCamps/Modifier/:id`} component={CampEditionProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Reservations`} component={Bookings} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Reservations/Créer`} component={BookingsProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Reservations/Modifier/:id`} component={BookingsProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Activites`} component={Activity} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Activites/Créer`} component={ActivityProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Activites/Modifier/:id`} component={ActivityProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Pieces`} component={Inside} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Pieces/Créer`} component={InsideProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Pieces/Modifier/:id`} component={InsideProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Comite`} component={Comite} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Comite/Créer`} component={ComiteProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Comite/Modifier/:id`} component={ComiteProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Textes`} component={Texts} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Textes/Créer`} component={TextsProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Textes/Modifier/:id`} component={TextsProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Historique`} component={History} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Historique/Créer`} component={HistoryProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Historique/Modifier/:id`} component={HistoryProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/News`} component={News} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/News/Créer`} component={NewsProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/News/Modifier/:id`} component={NewsProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Retrospective`} component={Retrospective} />
                </div>
              </div>
              {this.props.overlay.overlay.show &&
                <Fade in={true}>
                  <div id="overlay">
                    <CircularProgress color="primary" thickness={3} size={75} />
                    <Typography color="primary" variant="body1">{this.props.overlay.overlay.text}</Typography>
                  </div>
                </Fade>
              }
              <Notification
                className={this.props.notification.notification.className}
                open={this.props.notification.notification.show}
                text={this.props.notification.notification.text} />
            </div>
          </MuiPickersUtilsProvider>
        </MuiThemeProvider>
      </Router>
    );
  }
});



// WEBPACK FOOTER //
// ./src/app/App.tsx